import Store from '@ember-data/store';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import V2TaskModel from 'donor-elf-web/models/v2-task';
import type { onTaskSavingArgs } from '../task/info';

interface ComponentArgs {
  initialTasks: V2TaskModel[];
  initialUnfinishedTasksCount: number;
}

export default class DashboardTasksComponents extends Component<ComponentArgs> {
  constructor(owner: any, args: ComponentArgs) {
    super(owner, args);
    this.tasks = args.initialTasks;
    this.unfinishedTasksCount = args.initialUnfinishedTasksCount;
    this.hasMoreTasks =
      this.unfinishedTasksCount >
      this.tasks.filter((x) => !x.isComplete).length;
  }

  @service declare store: Store;

  @tracked tasks: V2TaskModel[] = [];
  @tracked unfinishedTasksCount: number = 0;
  @tracked hasMoreTasks = false;
  @tracked loading = false;
  @tracked error: any = null;

  // keeps track of the task ids that have been marked complete
  taskIdsCompleted: { [taskId: string]: boolean } = {};

  @action
  handleTaskSaving(args: onTaskSavingArgs) {
    if (args.task.isComplete) {
      if (this.taskIdsCompleted[args.task.id] != true) {
        this.unfinishedTasksCount -= 1;
        this.taskIdsCompleted[args.task.id] = true;
      }
    } else if (this.taskIdsCompleted[args.task.id]) {
      // if it was marked as complete but now is not, adjust the task count
      this.unfinishedTasksCount += 1;
      delete this.taskIdsCompleted[args.task.id];
    }
  }

  @action
  handleTaskSaved(_task: V2TaskModel) {
    // not doing anything but is required by <Task::Info />
  }

  @action
  handleTaskDeleted(task: V2TaskModel) {
    this.tasks = this.tasks.filter((x) => x.id !== task.id);
    this.unfinishedTasksCount -= 1;
  }

  @action
  async loadMore() {
    this.loading = true;
    let currentTaskIds = this.tasks.map((x) => x.id);
    try {
      let newTasksResponse = await this.store.query('v2-task', {
        filter: {
          completed: 'Not Complete',
          exclude_closed_journals: true,
          limit: 5,
          exclude_ids: currentTaskIds,
        },
        sort: 'due_date',
      });
      this.tasks = [...this.tasks, ...newTasksResponse.slice()];
      this.unfinishedTasksCount = newTasksResponse.meta.total_count;
      this.hasMoreTasks =
        this.unfinishedTasksCount >
        this.tasks.filter((x) => !x.isComplete).length;
    } catch (error) {
      this.error = error;
    }

    this.loading = false;
  }
}
